import { useQuery } from "@tanstack/react-query";
import { opsMeQuery } from "../../../api/queries";
import type { components } from "../../../api/generated/vision-api";

export type OpsRole = components["schemas"]["OpsMe"]["ops_roles"][number];

// ops-support (support-access SUP-01) is a separate preset, not a step above viewer: it ranks below every other role.
const RANK: Record<OpsRole, number> = { "ops-support": 0, "ops-viewer": 1, "ops-engineer": 2, "ops-admin": 3 };

/**
 * The highest console role in a list (P-107); a viewer when the list is empty. A support-only operator stays
 * ops-support (SUP-01): the search starts from the list's first role, not from viewer.
 */
export function topRole(roles: readonly OpsRole[] | undefined): OpsRole {
  const [first, ...rest] = roles ?? [];
  if (!first) return "ops-viewer";
  return rest.reduce<OpsRole>((best, r) => (RANK[r] > RANK[best] ? r : best), first);
}

/** Whether `role` includes everything `needed` may do: engineer includes viewer, admin includes both. */
export function roleAllows(role: OpsRole, needed: OpsRole): boolean {
  return RANK[role] >= RANK[needed];
}

/**
 * The operator's console role (WEB-DBG-AC-02). It gates what the screens show; the server decides, and a refused
 * action comes back as a 403 problem.
 */
export function useOpsRole(): { role: OpsRole; can: (needed: OpsRole) => boolean } {
  const me = useQuery(opsMeQuery());
  const role = topRole(me.data?.ops_roles);
  return { role, can: (needed) => roleAllows(role, needed) };
}
